import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useBudget } from '../../contexts/BudgetContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import LockedFeature from '../Subscription/LockedFeature';
import api from '../../services/api';
import {
  AnnualCard,
  AutomationCard,
  BUCKET_COLOR,
  Card,
  DebtCard,
  GoalsCard,
  HistoryCard,
  PayCycleCard,
  ScenarioCard,
  SharedCard,
  SurplusCard,
} from './PaycheckSections';
const BUCKETS = ['needs', 'wants', 'savings'];
const LINE_KEYS = {
  needs: ['housing', 'utilities', 'groceries', 'transport', 'health', 'phone', 'debt', 'annual', 'buffer'],
  wants: ['dining', 'leisure', 'clothes', 'travel', 'gifts'],
  savings: ['savings'],
};
const TIER_EN = { 1: 'Cheaper city', 2: 'Mid-cost city', 3: 'Expensive city', 4: 'Very expensive city' };
const TIER_CAP = { 1: 0.28, 2: 0.32, 3: 0.35, 4: 0.38 };
const CITY_EXAMPLES = { 1: 'Naples, Porto, Leipzig', 2: 'Turin, Valencia, Lyon', 3: 'Milan, Barcelona, Berlin', 4: 'Paris, Munich, Amsterdam' };

const LINE_EN = {
  housing: 'Housing', utilities: 'Utilities', groceries: 'Groceries', transport: 'Transport', health: 'Health',
  phone: 'Phone + internet', debt: 'Debt payments', annual: 'Yearly costs', buffer: 'Buffer', dining: 'Dining out',
  leisure: 'Leisure', clothes: 'Clothes', travel: 'Travel', gifts: 'Gifts', savings: 'Savings',
  other_needs: 'Other needs', other_wants: 'Other wants', exclude: 'Not spending (skip)',
};
const VERDICT_EN = {
  shortfall: 'Fixed costs are more than this paycheck',
  overspending: 'You spend more than you take home',
  housing: 'Housing is the problem',
  borderline: 'Borderline — livable, but one shock will hurt',
  planOnly: 'Your plan balances',
  comfortable: 'Comfortable',
};
const CHECK_EN = {
  housing: 'Housing within the city cap',
  needs: 'Needs within target',
  wants: 'Wants within target',
  savings: 'Saving at the target',
  leftover: 'Not overspending',
};
const STATUS_EN = { pass: 'OK', over: 'Over', low: 'Low', high: 'High', unknown: 'No data' };

function monthKeys() {
  const now = new Date();
  const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { current: key(now), next: key(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
}

function SplitBar({ parts, height = 12 }) {
  const total = parts.reduce((s, p) => s + Math.max(0, p.value), 0) || 1;
  return (
    <Box sx={{ display: 'flex', height, borderRadius: 99, overflow: 'hidden', bgcolor: 'action.hover' }}>
      {parts.map((p) => (
        <Tooltip key={p.key} title={p.label}>
          <Box sx={{ width: `${(Math.max(0, p.value) / total) * 100}%`, bgcolor: p.color, transition: 'width .3s' }} />
        </Tooltip>
      ))}
    </Box>
  );
}

function LineBar({ actual, planned }) {
  const cap = Math.max(actual || 0, planned || 0, 1);
  const over = (actual || 0) > (planned || 0) + 1;
  return (
    <Box sx={{ position: 'relative', height: 6, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden', mt: 0.75 }}>
      <Box sx={{ position: 'absolute', inset: 0, width: `${Math.min(100, ((planned || 0) / cap) * 100)}%`, bgcolor: 'rgba(15,118,110,0.3)' }} />
      {actual != null && (
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, ((actual || 0) / cap) * 100)}%`, bgcolor: over ? 'error.main' : '#0f766e' }} />
      )}
    </Box>
  );
}

const FREQ_EN = { monthly: 'Monthly', biweekly: 'Every two weeks', weekly: 'Weekly', irregular: 'Irregular' };
const EMPTY_FORM = {
  income: '', household: 1, tier: 2, car: false, rent: '', debtMin: '',
  frequency: 'monthly', payDay: '', payAnchor: '', incomeMode: 'typical', city: '',
  partner: false, partnerIncome: '', annual: '', extraDebt: 0, debtMethod: 'avalanche',
};

const titleCase = (s) => s.replace(/(^|[\s-])(\S)/g, (m) => m.toUpperCase());

/** Values from the form as the API expects them. */
function toBody(values, mapping, month, goalSplit) {
  const num = (v) => (v === '' || v == null ? null : Number(v));
  return {
    income: Number(values.income),
    household: Number(values.household) || 1,
    tier: Number(values.tier),
    car: values.car,
    rent: num(values.rent),
    debtMin: num(values.debtMin),
    annual: num(values.annual),
    extraDebt: Number(values.extraDebt) || 0,
    debtMethod: values.debtMethod,
    partnerIncome: values.partner ? num(values.partnerIncome) : null,
    frequency: values.frequency,
    payDay: values.frequency === 'monthly' ? num(values.payDay) : null,
    payAnchor: ['weekly', 'biweekly'].includes(values.frequency) && values.payAnchor ? values.payAnchor : null,
    incomeMode: values.incomeMode,
    city: values.city || '',
    mapping,
    month,
    ...(goalSplit ? { goalSplit } : {}),
  };
}

const PaycheckPlanPage = () => {
  const { t, i18n } = useTranslation();
  const { formatAmount, currency } = useCurrency();
  const { syncWithServer } = useBudget();
  const { hasFeature } = useSubscription();
  const unlocked = hasFeature('paycheckPlan');
  const months = useMemo(monthKeys, []);

  const [form, setForm] = useState(EMPTY_FORM);
  const [cities, setCities] = useState({});
  const [goalSplit, setGoalSplit] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [month, setMonth] = useState(months.current);
  const [detected, setDetected] = useState(null);
  const [booting, setBooting] = useState(true);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createMissing, setCreateMissing] = useState(false);
  const [review, setReview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [snack, setSnack] = useState(null);
  const requestId = useRef(0);

  const lineName = useCallback((key) => t(`paycheck.line.${key}`, LINE_EN[key] || key), [t]);
  const pct = (n) => `${Math.round((n || 0) * 100)}%`;
  const monthLabel = useCallback((key) => {
    const [y, m] = key.split('-').map(Number);
    return new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 1));
  }, [i18n.language]);

  const localNote = useCallback((plan) => {
    const over = (plan.lines || [])
      .filter((l) => l.variance != null && l.variance < -1)
      .sort((a, b) => a.variance - b.variance)
      .slice(0, 3)
      .map((l) => `${lineName(l.key)} (${formatAmount(Math.abs(l.variance))})`)
      .join(', ');
    const code = plan.score?.verdict?.code;
    const amount = formatAmount(plan.score?.verdict?.amount || 0);
    const base = t(`paycheck.note.${code}`, {
      amount,
      defaultValue: {
        shortfall: 'Rent and debt minimums need {{amount}} more than you take home. Talk to your lender about the payments, or lower the rent.',
        overspending: 'On a typical month you spend {{amount}} more than you take home. Cut wants first, then look at the fixed costs.',
        housing: 'Housing takes more than the cap for this city tier. A cheaper place or a flatmate changes more than cutting coffee.',
        borderline: 'The month works, but there is little room for a surprise. Bring the lines over plan back down and protect savings.',
        planOnly: 'Nothing to compare yet. Sort your categories below, or come back after a month of tracking.',
        comfortable: 'Your typical month fits this income. Keep the savings line automatic.',
      }[code] || '',
    });
    return over ? `${base} ${t('paycheck.note.over', { lines: over, defaultValue: 'Over plan: {{lines}}.' })}` : base;
  }, [formatAmount, lineName, t]);

  const loadCoach = useCallback(async (plan, id) => {
    setCoach(null);
    setCoachLoading(true);
    try {
      const res = await api.post('/paycheck/coach', {
        plan: { inputs: plan.inputs, score: plan.score, lines: plan.lines, shortfall: plan.shortfall },
        locale: i18n.language,
        currency,
      }, { timeout: 8000 });
      if (id === requestId.current) setCoach(res.data?.data?.text || null);
    } catch (_) {
      if (id === requestId.current) setCoach(null);
    } finally {
      if (id === requestId.current) setCoachLoading(false);
    }
  }, [currency, i18n.language]);

  const build = useCallback(async (values, nextMapping, targetMonth, split) => {
    const income = Number(values.income);
    if (!Number.isFinite(income) || income <= 0) {
      setError(t('paycheck.incomeRequired', 'Enter a monthly take-home greater than 0'));
      return;
    }
    const id = ++requestId.current;
    setError('');
    setLoading(true);
    try {
      const body = toBody(values, nextMapping, targetMonth, split);
      const res = await api.post('/paycheck/plan', body, { timeout: 20000 });
      if (id !== requestId.current) return;
      const data = res.data?.data || res.data;
      setResult(data);
      loadCoach(data, id);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.code === 'ECONNABORTED'
        ? t('paycheck.timeout', 'The plan took too long. Try again in a moment.')
        : t('paycheck.buildError', 'Could not build the plan. Try again in a moment.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [loadCoach, t]);

  // Prefill from the last plan, or from what the app can see in the user's data.
  useEffect(() => {
    if (!unlocked) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/paycheck/state', { timeout: 15000 });
        if (!alive) return;
        const { saved, detected: found, cities: cityTiers } = res.data?.data || {};
        setDetected(found || null);
        setCities(cityTiers || {});
        const src = saved || {};
        const conservative = src.incomeMode === 'conservative' || (!saved && found?.irregular);
        const values = {
          ...EMPTY_FORM,
          income: src.income ?? (conservative ? found?.conservativeIncome : found?.income) ?? '',
          household: src.household ?? 1,
          tier: src.tier ?? 2,
          car: !!src.car,
          rent: src.rent ?? found?.rent ?? '',
          debtMin: src.debtMin ?? found?.debtMin ?? '',
          annual: src.annual ?? '',
          extraDebt: src.extraDebt || 0,
          debtMethod: src.debtMethod || 'avalanche',
          partner: src.partnerIncome > 0,
          partnerIncome: src.partnerIncome ?? '',
          frequency: src.frequency || 'monthly',
          payDay: src.payDay ?? '',
          payAnchor: src.payAnchor ? String(src.payAnchor).slice(0, 10) : '',
          incomeMode: conservative ? 'conservative' : 'typical',
          city: src.city || '',
        };
        setForm(values);
        if (saved) build(values, {}, months.current);
      } catch (_) {
        /* the form still works without prefill */
      } finally {
        if (alive) setBooting(false);
      }
    })();
    return () => { alive = false; };
  }, [unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = (e) => {
    e?.preventDefault();
    setSnack(null);
    build(form, mapping, month, goalSplit);
  };

  const changeMapping = (categoryId, target) => {
    const next = { ...mapping, [categoryId]: target };
    setMapping(next);
    build(form, next, month, goalSplit);
  };

  const changeMonth = (_, value) => {
    if (!value) return;
    setMonth(value);
    if (result) build(form, mapping, value, goalSplit);
  };

  // Changes made inside the result cards rebuild the plan straight away.
  const changePlan = (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    build(next, mapping, month, goalSplit);
  };

  const changeSplit = (key, amount) => {
    const current = Object.fromEntries((result?.goals?.split?.rows || []).map((r) => [r.emergency ? 'emergency' : r.id, r.amount]));
    const next = { ...current, ...(goalSplit || {}), [key]: amount };
    setGoalSplit(next);
    build(form, mapping, month, next);
  };

  const pickCity = (_, value) => {
    const city = typeof value === 'string' ? value : '';
    const tier = cities[city.trim().toLowerCase()];
    setForm((f) => ({ ...f, city, ...(tier ? { tier } : {}) }));
  };

  const setLeanIncome = (on) => {
    setForm((f) => ({
      ...f,
      incomeMode: on ? 'conservative' : 'typical',
      ...(detected?.conservativeIncome && detected?.income
        ? { income: on ? detected.conservativeIncome : detected.income }
        : {}),
    }));
  };

  // What "apply" would write: every mapped category, plus new ones for untracked lines.
  const changes = useMemo(() => {
    if (!result) return { rows: [], create: [] };
    const rows = (result.lines || []).flatMap((line) => line.categories.map((c) => ({
      categoryId: c.categoryId,
      name: c.name,
      line: line.key,
      before: c.currentBudget,
      after: c.suggested,
    })));
    const create = createMissing
      ? (result.lines || [])
        .filter((line) => !line.categories.length && line.suggested > 0)
        .map((line) => ({ key: line.key, name: lineName(line.key), amount: line.suggested }))
      : [];
    return { rows, create };
  }, [createMissing, lineName, result]);

  const untracked = (result?.lines || []).filter((l) => !l.categories.length && l.suggested > 0);
  const cityOptions = useMemo(() => Array.from(new Set(Object.keys(cities).map(titleCase))).sort(), [cities]);
  const planBody = useMemo(() => (result ? toBody(form, mapping, month) : null), [form, mapping, month, result]);

  const apply = async () => {
    setApplying(true);
    try {
      const res = await api.post('/paycheck/apply', {
        month: result.month,
        allocations: changes.rows.map((r) => ({ categoryId: r.categoryId, amount: r.after })),
        create: changes.create,
        summary: {
          income: result.inputs.income,
          tier: result.inputs.tier,
          household: result.inputs.household,
          frequency: result.settings?.frequency,
          partnerIncome: form.partner ? Number(form.partnerIncome) || null : null,
          buckets: result.buckets,
          planSavingsShare: (result.buckets.savings + (result.adjustments?.toDebt || 0)) / result.inputs.income,
          actualSavingsShare: result.score?.anyActuals ? result.score.savingsShare : null,
          verdict: result.score?.verdict?.code,
        },
      });
      const d = res.data?.data || {};
      setReview(false);
      setSnack({
        message: t('paycheck.appliedMonth', {
          count: d.updated + d.created,
          month: monthLabel(d.month),
          defaultValue: 'Saved {{count}} budgets for {{month}}',
        }),
        undo: { month: d.month, previous: d.previous, createdIds: d.createdIds, snapshotId: d.snapshotId },
      });
      setHistoryKey((k) => k + 1);
      syncWithServer?.().catch(() => {});
      build(form, mapping, month, goalSplit);
    } catch (err) {
      setReview(false);
      setError(err.response?.data?.code === 'no_categories'
        ? t('paycheck.noCategories', 'Add expense categories first')
        : t('paycheck.applyError', 'Could not save the budgets. Nothing was changed.'));
    } finally {
      setApplying(false);
    }
  };

  const undo = async () => {
    const payload = snack?.undo;
    setSnack(null);
    if (!payload) return;
    try {
      await api.post('/paycheck/restore', payload);
      setSnack({ message: t('paycheck.undone', 'Budgets put back as they were') });
      setHistoryKey((k) => k + 1);
      syncWithServer?.().catch(() => {});
      build(form, mapping, month, goalSplit);
    } catch (_) {
      setError(t('paycheck.undoError', 'Could not undo. Check the Budgets page.'));
    }
  };

  if (!unlocked) {
    return (
      <LockedFeature feature="paycheckPlan" ctaPlan="premium" preview="overlay" promoText={t('paycheck.locked', 'Paycheck plan is Premium — see a safe split of your take-home')}>
        <Box sx={{ minHeight: 360, borderRadius: 3, bgcolor: 'action.hover' }} />
      </LockedFeature>
    );
  }

  const score = result?.score;
  const verdict = score?.verdict;
  const statusColor = (s) => ({ pass: 'success', unknown: 'default' }[s] || 'warning');
  const hasActuals = !!score?.anyActuals;
  const adj = result?.adjustments || {};
  const mapOptions = [
    ...BUCKETS.flatMap((b) => [
      <ListSubheader key={`h-${b}`}>{t(`paycheck.bucket.${b}`, b[0].toUpperCase() + b.slice(1))}</ListSubheader>,
      ...LINE_KEYS[b].map((k) => <MenuItem key={k} value={k}>{lineName(k)}</MenuItem>),
    ]),
    <ListSubheader key="h-other">{t('paycheck.bucket.other', 'Other')}</ListSubheader>,
    ...['other_needs', 'other_wants', 'exclude'].map((k) => <MenuItem key={k} value={k}>{lineName(k)}</MenuItem>),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 960, mx: 'auto', pb: { xs: '96px', md: 4 } }}>
      <Box>
        <Typography variant="h5" fontWeight={800}>{t('paycheck.title', 'Paycheck plan')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('paycheck.lead2', 'Split your take-home into needs, wants and savings, compare it with how you really spend, then save it as this month’s budgets.')}
        </Typography>
      </Box>

      {/* Step 1: inputs */}
      <Card>
        <Box component="form" onSubmit={submit} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
          <TextField
            label={t('paycheck.income', 'Monthly take-home')}
            type="number"
            value={form.income}
            onChange={setField('income')}
            required
            disabled={booting}
            inputProps={{ min: 1, step: '0.01', inputMode: 'decimal' }}
            helperText={detected?.income
              ? t('paycheck.incomeDetected', { amount: formatAmount(detected.income), count: detected.months, defaultValue: 'Your typical income: {{amount}} (last {{count}} months)' })
              : t('paycheck.incomeHelp', 'After tax, what lands in your account each month')}
          />
          <FormControl>
            <InputLabel>{t('paycheck.tier', 'City cost')}</InputLabel>
            <Select
              label={t('paycheck.tier', 'City cost')}
              value={form.tier}
              onChange={(e) => setForm((f) => ({ ...f, tier: Number(e.target.value) }))}
              renderValue={(n) => `${t(`paycheck.tier${n}`, TIER_EN[n])} · ${CITY_EXAMPLES[n]}`}
            >
              {[1, 2, 3, 4].map((n) => (
                <MenuItem key={n} value={n}>
                  <Box>
                    <Typography variant="body2">{t(`paycheck.tier${n}`, TIER_EN[n])}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">{t('paycheck.eg', 'e.g.')} {CITY_EXAMPLES[n]}</Typography>
                    {Number(form.income) > 0 && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {t('paycheck.tierCap', { amount: formatAmount(Number(form.income) * TIER_CAP[n]), defaultValue: 'Housing up to {{amount}}' })}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('paycheck.rent', 'Rent or mortgage')}
            type="number"
            value={form.rent}
            onChange={setField('rent')}
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
            helperText={detected?.rent
              ? t('paycheck.rentDetected', { amount: formatAmount(detected.rent), defaultValue: 'From your housing spending: {{amount}}' })
              : t('paycheck.rentHelp', 'Leave empty to use the city cap')}
          />
          <TextField
            label={t('paycheck.debtMin', 'Minimum debt payments')}
            type="number"
            value={form.debtMin}
            onChange={setField('debtMin')}
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
            helperText={detected?.debtMin
              ? t('paycheck.debtDetected', { amount: formatAmount(detected.debtMin), defaultValue: 'From your debt accounts: {{amount}}' })
              : t('paycheck.debtHelp', 'Loans, cards, buy-now-pay-later')}
          />
          <TextField
            label={t('paycheck.household', 'People in the household')}
            type="number"
            value={form.household}
            onChange={setField('household')}
            inputProps={{ min: 1, max: 12, step: 1 }}
          />
          <FormControlLabel sx={{ ml: 0 }} control={<Switch checked={form.car} onChange={setField('car')} />} label={t('paycheck.car', 'I run a car')} />
          <Autocomplete
            freeSolo
            options={cityOptions}
            value={form.city}
            onChange={pickCity}
            onInputChange={(e, value, reason) => reason === 'input' && setForm((f) => ({ ...f, city: value }))}
            onBlur={() => pickCity(null, form.city)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('paycheck.city', 'City (optional)')}
                helperText={cities[form.city.trim().toLowerCase()]
                  ? t('paycheck.cityTier', { tier: t(`paycheck.tier${cities[form.city.trim().toLowerCase()]}`, TIER_EN[cities[form.city.trim().toLowerCase()]]), defaultValue: 'Sets the city cost to: {{tier}}' })
                  : t('paycheck.cityHelp', 'Pick your city to set the city cost')}
              />
            )}
          />
          <TextField
            label={t('paycheck.annualField', 'Yearly costs, per month')}
            type="number"
            value={form.annual}
            onChange={setField('annual')}
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
            helperText={detected?.annual
              ? t('paycheck.annualDetected', { amount: formatAmount(detected.annual), defaultValue: 'From your yearly bills: {{amount}} — leave empty to use it' })
              : t('paycheck.annualHelp', 'Insurance, car tax, subscriptions paid once a year, divided by 12')}
          />
          <FormControl>
            <InputLabel>{t('paycheck.frequency', 'I get paid')}</InputLabel>
            <Select label={t('paycheck.frequency', 'I get paid')} value={form.frequency} onChange={setField('frequency')}>
              {Object.keys(FREQ_EN).map((f) => <MenuItem key={f} value={f}>{t(`paycheck.freq.${f}`, FREQ_EN[f])}</MenuItem>)}
            </Select>
          </FormControl>
          {form.frequency === 'monthly' && (
            <TextField
              label={t('paycheck.payDay', 'Payday (day of the month)')}
              type="number"
              value={form.payDay}
              onChange={setField('payDay')}
              inputProps={{ min: 1, max: 31, step: 1 }}
              helperText={t('paycheck.payDayHelp', 'Used to show the bills due before your next paycheck')}
            />
          )}
          {['weekly', 'biweekly'].includes(form.frequency) && (
            <TextField
              label={t('paycheck.payAnchor', 'Date of any payday')}
              type="date"
              value={form.payAnchor}
              onChange={setField('payAnchor')}
              InputLabelProps={{ shrink: true }}
              helperText={t('paycheck.payAnchorHelp', 'The last or the next one, both work')}
            />
          )}
          {(form.frequency === 'irregular' || detected?.irregular || form.incomeMode === 'conservative') && (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={<Switch checked={form.incomeMode === 'conservative'} onChange={(e) => setLeanIncome(e.target.checked)} />}
              label={(
                <Box>
                  <Typography variant="body2">{t('paycheck.lean', 'Plan on a lean month')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detected?.conservativeIncome
                      ? t('paycheck.leanHelp', { amount: formatAmount(detected.conservativeIncome), defaultValue: 'Your income varies. A lean month for you is about {{amount}}.' })
                      : t('paycheck.leanHelpNone', 'For income that changes month to month')}
                  </Typography>
                </Box>
              )}
            />
          )}
          <FormControlLabel
            sx={{ ml: 0 }}
            control={<Switch checked={form.partner} onChange={setField('partner')} />}
            label={t('paycheck.partner', 'I share costs with a partner')}
          />
          {form.partner && (
            <TextField
              label={t('paycheck.partnerIncome', 'Partner’s monthly take-home')}
              type="number"
              value={form.partnerIncome}
              onChange={setField('partnerIncome')}
              inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
              helperText={t('paycheck.partnerHelp', 'Rent above is then the whole household’s rent; you pay your share')}
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ gridColumn: { sm: '1 / -1' } }}>
            <ToggleButtonGroup exclusive size="small" value={month} onChange={changeMonth} aria-label={t('paycheck.forMonth', 'Plan for')}>
              <ToggleButton value={months.current}>{monthLabel(months.current)}</ToggleButton>
              <ToggleButton value={months.next}>{monthLabel(months.next)}</ToggleButton>
            </ToggleButtonGroup>
            <Button type="submit" variant="contained" disabled={loading || booting} sx={{ height: 44, flex: 1 }}>
              {loading ? <CircularProgress size={18} color="inherit" /> : (result ? t('paycheck.rebuild', 'Update plan') : t('paycheck.build', 'Build plan'))}
            </Button>
          </Stack>
        </Box>
      </Card>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {!result && (loading || booting) && (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={88} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={260} />
        </Stack>
      )}

      {result && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
          {/* Step 2: verdict */}
          <Alert severity={verdict?.level || 'info'} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
            <AlertTitle sx={{ fontWeight: 800 }}>{t(`paycheck.verdict.${verdict?.code}`, VERDICT_EN[verdict?.code] || '')}</AlertTitle>
            {coachLoading ? <Skeleton width="90%" /> : (coach || localNote(result))}
          </Alert>

          <PayCycleCard cycle={result.cycle} />

          {/* The split, and how the typical month compares */}
          <Card>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography fontWeight={800}>{t('paycheck.planned', 'Your plan')}</Typography>
              <Typography variant="caption" color="text.secondary">{formatAmount(result.inputs.income)} / {t('paycheck.month', 'month')}</Typography>
            </Stack>
            <SplitBar parts={BUCKETS.map((b) => ({ key: b, value: result.buckets[b], color: BUCKET_COLOR[b], label: t(`paycheck.bucket.${b}`, b) }))} />
            {hasActuals && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                  {t('paycheck.typical', { count: result.actuals.months, defaultValue: 'Your typical month (last {{count}} months)' })}
                </Typography>
                <SplitBar parts={[
                  { key: 'needs', value: score.needsShare, color: BUCKET_COLOR.needs, label: t('paycheck.bucket.needs', 'Needs') },
                  { key: 'wants', value: score.wantsShare, color: BUCKET_COLOR.wants, label: t('paycheck.bucket.wants', 'Wants') },
                  score.saved >= 0
                    ? { key: 'saved', value: score.savingsShare, color: BUCKET_COLOR.savings, label: t('paycheck.bucket.savings', 'Savings') }
                    : { key: 'over', value: -score.savingsShare, color: '#dc2626', label: t('paycheck.overspent', 'Overspent') },
                ]}
                />
              </>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25, mt: 2 }}>
              {BUCKETS.map((b) => {
                const actualShare = hasActuals ? score[`${b}Share`] : null;
                return (
                  <Box key={b} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BUCKET_COLOR[b] }}>
                      {t(`paycheck.bucket.${b}`, b)}
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>{formatAmount(result.buckets[b])}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t('paycheck.target', { pct: pct(result.targets[`${b}Pct`]), defaultValue: 'Target {{pct}} of income' })}
                    </Typography>
                    {actualShare != null && (
                      <Typography variant="caption" component="div" sx={{ fontWeight: 700 }}>
                        {t('paycheck.youShare', { pct: pct(actualShare), defaultValue: 'You: {{pct}}' })}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {Object.entries(score?.checks || {}).map(([key, status]) => (
                <Chip
                  key={key}
                  size="small"
                  variant={status === 'unknown' ? 'outlined' : 'filled'}
                  color={statusColor(status)}
                  label={`${t(`paycheck.check.${key}`, CHECK_EN[key] || key)} · ${t(`paycheck.status.${status}`, STATUS_EN[status] || status)}`}
                />
              ))}
            </Stack>
          </Card>

          {(adj.freed > 0 || adj.fromWants > 0 || adj.fromSavings > 0) && (
            <Alert severity={adj.shortfall > 0 ? 'error' : 'info'}>
              {adj.freed > 0 && t('paycheck.adjFreed', { amount: formatAmount(adj.freed), defaultValue: 'Your rent is under the cap, so {{amount}} moves to savings.' })}
              {(adj.fromWants > 0 || adj.fromSavings > 0) && t('paycheck.adjCut', {
                wants: formatAmount(adj.fromWants),
                savings: formatAmount(adj.fromSavings),
                defaultValue: 'Rent and debt payments above the template are paid from wants ({{wants}}) and savings ({{savings}}). Groceries and bills are not cut.',
              })}
              {adj.shortfall > 0 && ` ${t('paycheck.adjShort', { amount: formatAmount(adj.shortfall), defaultValue: 'Still missing: {{amount}}.' })}`}
            </Alert>
          )}

          {/* Plan lines by bucket */}
          {BUCKETS.map((b) => (
            <Card key={b} sx={{ p: { xs: 1.25, sm: 1.75 } }}>
              <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: BUCKET_COLOR[b] }}>{t(`paycheck.bucket.${b}`, b)}</Typography>
                <Typography variant="subtitle2" fontWeight={800}>{formatAmount(result.buckets[b])}</Typography>
              </Stack>
              <Stack spacing={0.75}>
                {result.lines.filter((l) => l.bucket === b && (l.suggested > 0 || l.actual)).map((line) => {
                  const over = line.variance != null && line.variance < -1;
                  return (
                    <Box key={line.key} sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: over ? 'error.light' : 'divider' }}>
                      <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography fontWeight={700} noWrap>{lineName(line.key)}</Typography>
                            {line.fixed && <Chip size="small" label={t('paycheck.fixed', 'fixed')} sx={{ height: 18, fontSize: 11 }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" component="div" noWrap>
                            {line.categories.length
                              ? line.categories.map((c) => c.name).join(', ')
                              : t('paycheck.noCategory', 'No category yet')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography fontWeight={800}>{formatAmount(line.suggested)}</Typography>
                          <Typography variant="caption" color={over ? 'error.main' : 'text.secondary'}>
                            {line.actual == null
                              ? t('paycheck.noActual', 'No history')
                              : t(over ? 'paycheck.youOver' : 'paycheck.youUnder', {
                                amount: formatAmount(line.actual),
                                diff: formatAmount(Math.abs(line.variance)),
                                defaultValue: over ? 'You {{amount}} · {{diff}} over' : 'You {{amount}} · {{diff}} under',
                              })}
                          </Typography>
                        </Box>
                      </Stack>
                      <LineBar actual={line.actual} planned={line.suggested} />
                    </Box>
                  );
                })}
              </Stack>
            </Card>
          ))}

          {/* Category sorting */}
          {!!result.categories?.length && (
            <Accordion disableGutters sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={800}>{t('paycheck.sortTitle', 'Your categories')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('paycheck.sortLead', 'Check where each category counts. Changes update the plan and are remembered.')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={1}>
                  {result.categories.map((c) => (
                    <Stack key={c.categoryId} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography fontWeight={600} noWrap>{c.name}</Typography>
                          {c.auto && <Chip size="small" variant="outlined" label={t('paycheck.auto', 'guessed')} sx={{ height: 18, fontSize: 11 }} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {c.typical > 0
                            ? t('paycheck.perMonth', { amount: formatAmount(c.typical), defaultValue: '{{amount}} / month' })
                            : t('paycheck.noSpend', 'No spending yet')}
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: { sm: 240 } }}>
                        <Select value={c.target} onChange={(e) => changeMapping(c.categoryId, e.target.value)} disabled={loading}>
                          {mapOptions}
                        </Select>
                      </FormControl>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          <GoalsCard result={result} onSplit={changeSplit} onCreated={() => build(form, mapping, month, goalSplit)} />

          <DebtCard debt={result.debt} extra={form.extraDebt} method={form.debtMethod} onChange={changePlan} />

          <SharedCard shared={result.shared} />

          <AnnualCard annualCosts={result.annualCosts} line={result.lines.find((l) => l.key === 'annual')} />

          <SurplusCard surplus={result.surplus} profile={result.incomeProfile} income={result.inputs.income} />

          <ScenarioCard body={planBody} />

          {/* Step 3: save as budgets */}
          <Card>
            <Typography fontWeight={800}>{t('paycheck.applyTitle', { month: monthLabel(result.month), defaultValue: 'Use this plan for {{month}}' })}</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {t('paycheck.applyLead', 'Only this month’s category budgets change. Other months stay as they are, and you can undo.')}
            </Typography>
            {!!untracked.length && (
              <FormControlLabel
                control={<Checkbox checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />}
                label={t('paycheck.createMissing2', {
                  lines: untracked.map((l) => lineName(l.key)).join(', '),
                  defaultValue: 'Also create categories for lines you do not track: {{lines}}',
                })}
              />
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 1, height: 44 }}
              disabled={loading || (!changes.rows.length && !changes.create.length)}
              onClick={() => setReview(true)}
            >
              {t('paycheck.review', 'Review changes')}
            </Button>
            <AutomationCard settings={result.settings} />
          </Card>

          <HistoryCard refreshKey={historyKey} onUndone={() => { setSnack(null); syncWithServer?.().catch(() => {}); build(form, mapping, month, goalSplit); }} />

          <Typography variant="caption" color="text.secondary">
            {t('paycheck.disclaimer', 'Planning defaults for a comfortable life in an EU city. Not financial advice.')}
          </Typography>
        </Box>
      )}

      <Dialog open={review} onClose={() => !applying && setReview(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('paycheck.reviewTitle', { month: result ? monthLabel(result.month) : '', defaultValue: 'Budgets for {{month}}' })}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('paycheck.colCategory', 'Category')}</TableCell>
                <TableCell align="right">{t('paycheck.colNow', 'Now')}</TableCell>
                <TableCell align="right">{t('paycheck.colNew', 'New')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.rows.map((r) => (
                <TableRow key={r.categoryId}>
                  <TableCell>
                    {r.name}
                    <Typography variant="caption" color="text.secondary" component="div">{lineName(r.line)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.before == null ? '—' : formatAmount(r.before)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(r.after)}</TableCell>
                </TableRow>
              ))}
              {changes.create.map((r) => (
                <TableRow key={`new-${r.key}`}>
                  <TableCell>
                    {r.name} <Chip size="small" color="primary" label={t('paycheck.new', 'new')} sx={{ height: 18, fontSize: 11 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(r.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(false)} disabled={applying}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={apply} disabled={applying}>
            {applying ? <CircularProgress size={18} color="inherit" /> : t('paycheck.confirm', 'Save budgets')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={snack?.undo ? 10000 : 4000}
        onClose={(_, reason) => reason !== 'clickaway' && setSnack(null)}
        message={snack?.message}
        action={snack?.undo ? <Button color="inherit" size="small" onClick={undo}>{t('paycheck.undo', 'Undo')}</Button> : null}
        sx={{ bottom: { xs: 88, md: 24 } }}
      />
    </Box>
  );
};

export default PaycheckPlanPage;
